// Note: AdaptiveLearningEngine removed - all logic now in backend

export type ImprovementStatus = 'improving' | 'steady' | 'declining';

// Base performance interface
export interface CategoryPerformance {
  category: string;
  correctCount: number;
  totalCount: number;
  averageTime: number;
  lastAttempted: Date;
}

export interface CategoryStrength {
  correct: number;
  total: number;
  averageTime: number;
}

export interface QuestionAttempt {
    date: Date;
    isCorrect: boolean;
    timeSpent: number; // in seconds
    difficulty: number;
}

// Backend-driven adaptive state (pure data, no business logic)
export interface AdaptiveState {
  currentDifficulty: number;
  categoryStrengths: Record<string, CategoryPerformance>;
  insights: AdaptiveInsights | null;
  recentlyAskedQuestions: string[];
  consecutiveCorrect: number;
  consecutiveIncorrect: number;
  totalQuestionsAttempted: number;
}

// Storage state (without engine)
export interface StorageAdaptiveState {
  currentDifficulty: number;
  categoryStrengths: Record<string, StorageCategoryPerformance>;
  insights: AdaptiveInsights;
  recentlyAskedQuestions: string[];
  consecutiveCorrect: number;
  consecutiveIncorrect: number;
}

// Storage version of performance data
export interface StorageCategoryPerformance extends Omit<CategoryPerformance, 'lastAttempted'> {
  lastAttempted: string;  // ISO date string
}

export interface AdaptiveInsights {
    weakCategories: WeakCategory[];
    recommendedDifficulty: number;
    suggestedPracticeFrequency: string;
    performanceTrend: ImprovementStatus;
    timeManagement: TimeManagement;
}

export interface TimeManagement {
    averageQuestionTime: number;
    recommendedTimePerQuestion: number;
    timeManagementAdvice: string;
}

export interface WeakCategory {
    category: string;
    successRate: number;
    recommendedFocus: string[];
}

// Firebase serialization interfaces
export interface SerializableAdaptiveState {
    insights: AdaptiveInsights;
    currentDifficulty: number;
    categoryStrengths: {
        [key: string]: Omit<CategoryPerformance, 'lastAttempted'> & {
            lastAttempted: string;
        };
    };
    recentlyAskedQuestions: string[];
    consecutiveCorrect: number;
    consecutiveIncorrect: number;
}

// Helper functions to compute derived values
export const getCategorySuccessRate = (performance: CategoryPerformance): number => {
    return performance.correctCount / (performance.totalCount || 1);
};

export const getCategoryTotalAttempts = (performance: CategoryPerformance): number => {
    return performance.totalCount;
};

// Helper to convert between Date and string for Firebase
export const serializeCategoryPerformance = (
    performance: CategoryPerformance
): SerializableAdaptiveState['categoryStrengths'][string] => {
    return {
        ...performance,
        lastAttempted: performance.lastAttempted.toISOString()
    };
};

export const deserializeCategoryPerformance = (
    serialized: SerializableAdaptiveState['categoryStrengths'][string]
): CategoryPerformance => {
    return {
        ...serialized,
        lastAttempted: new Date(serialized.lastAttempted)
    };
};

export interface FirebaseAdaptiveState extends Omit<SerializableAdaptiveState, 'categoryStrengths'> {
    categoryStrengths: {
        [key: string]: CategoryPerformance;
    };
}

// Extended version for UI/Profile display
export interface EnhancedCategoryPerformance extends CategoryPerformance {
    improvement: ImprovementStatus;
    successRate: number;
    difficultyLevel: number;
}

// Simplified version for quick strength checks
export interface CategoryStrengthMetrics {
    successRate: number;
    totalAttempts: number;
}

export interface StudyPlan {
    weeklyHours: number;
    recommendedPace: number;
    categoryPlans: CategoryPlan[];
    milestones: Milestone[];
}

export interface CategoryPlan {
    category: string;
    hoursPerWeek: number;
    focusAreas: string[];
    recommendedResources: string[];
}

export interface Milestone {
    category: string;
    date: string;
    targetScore: number;
    recommendedActions: string[];
    categoriesComplete: string[];
}

export interface CategoryMetrics {
  correct: number;
  total: number;
  averageTime: number;
  successRate: number;
}

export interface CategoryStrength {
  correctCount: number;
  totalCount: number;
  averageTime: number;
  lastAttempted?: string;
}

export interface RecentPerformance {
  date: string;
  score: number;
  category?: string;
  questionsAttempted: number;
  timeSpent?: number;
  difficulty?: number;
}

export interface DifficultyProgress {
  date: string;
  difficulty: number;
  successRate: number;
}

export interface Mastery {
  beginner: string[];      // Categories at beginner level
  intermediate: string[];  // Categories at intermediate level
  advanced: string[];      // Categories at advanced level
}

export interface PreferredTime {
  dayOfWeek: string;
  timeOfDay: string;
  averagePerformance: number;
}

export interface AdaptiveMetrics {
  startingDifficulty: number;
  endingDifficulty: number;
  averageResponseTime: number;
  categoryPerformance: Record<string, CategoryMetrics>;
  overallSuccessRate: number;
  totalQuestionsAttempted: number;
  currentStreak: number;
  longestStreak: number;
  lastSessionDate: string;
  weeklyActivityStreak: number;
  studyTimeThisWeek: number;
  estimatedExamReadiness: number;
  completedCategories: string[];
  mastery: Mastery;
  recentPerformance: RecentPerformance[];
  preferredTimes: PreferredTime[];
  difficultyProgression: DifficultyProgress[];
}

// Helper to create default metrics
export const createDefaultAdaptiveMetrics = (): AdaptiveMetrics => ({
  startingDifficulty: 5,
  endingDifficulty: 5,
  averageResponseTime: 75,
  categoryPerformance: {},
  overallSuccessRate: 0,
  totalQuestionsAttempted: 0,
  currentStreak: 0,
  longestStreak: 0,
  lastSessionDate: new Date().toISOString(),
  weeklyActivityStreak: 0,
  studyTimeThisWeek: 0,
  estimatedExamReadiness: 0,
  completedCategories: [],
  mastery: {
    beginner: [],
    intermediate: [],
    advanced: []
  },
  recentPerformance: [],
  preferredTimes: [],
  difficultyProgression: []
});